import { useEffect, useRef } from 'react';

type ChatScrollAnchorParams = {
  deps: React.DependencyList;
  isAwaitingAgentResponse: boolean;
  anchorId: string | null;
  anchorRef: React.RefObject<HTMLElement | null>;
};

function scrollToBottom(el: HTMLDivElement) {
  // Performance: Prefer smooth scroll API when available for better UX.
  // Fallback to direct assignment for older browsers.
  if (typeof el.scrollTo === 'function') {
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    return;
  }

  el.scrollTop = el.scrollHeight;
}

export function useChatScrollAnchor(
  params: ChatScrollAnchorParams,
): React.RefObject<HTMLDivElement> {
  const { deps, isAwaitingAgentResponse, anchorId, anchorRef } = params;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const lastAnchoredIdRef = useRef<string | null>(null);
  const hasMountedRef = useRef(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Business rule: On initial mount, always scroll to bottom to show latest content.
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      lastAnchoredIdRef.current = anchorId;
      scrollToBottom(el);
      return;
    }

    // Business rule: While awaiting response, scroll to bottom to follow thinking indicator.
    // Ensures user sees that system is processing their request.
    if (isAwaitingAgentResponse) {
      scrollToBottom(el);
      return;
    }

    // Invariant: Only scroll to anchor if ID changed to prevent unnecessary scroll operations.
    // Prevents duplicate scrolls when same message updates multiple times.
    if (!anchorId || anchorId === lastAnchoredIdRef.current) return;

    const anchorEl = anchorRef.current;
    if (!anchorEl) return;

    // Business rule: Anchor to latest agent message when response completes.
    // Provides stable scroll position at message boundary instead of bottom of container.
    lastAnchoredIdRef.current = anchorId;
    // Disabled this so last message stays on top of screen
    //anchorEl.scrollIntoView({ block: 'start', behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return scrollRef;
}
